import React, { useEffect, useState } from 'react';
import { Shield, Users, BookOpen, Calendar } from 'lucide-react';
import { mockData } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { useTranslate } from '../hooks/useTranslate';

// Composant pour une carte de statistique individuelle
const StatCard = ({ stat, animatedValue }) => {
  const { translatedText: labelTranslated } = useTranslate(stat.label);
  
  const getIcon = (iconUrl) => {
    return <img src={iconUrl} alt="Icon" className="w-8 h-8" />;
  };
  
  return (
    <div className="text-center group">
      <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 transform group-hover:-translate-y-2">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 transition-colors" 
             style={{ backgroundColor: 'rgb(196, 150, 96)' }}>
          <div style={{ color: 'white' }}>
            {getIcon(stat.icon)}
          </div>
        </div>
        
        <div 
          className="text-4xl lg:text-5xl font-bold mb-2"
          style={{ 
            fontFamily: '"Lora", serif',
            color: 'rgb(196, 150, 96)'
          }}
        >
          {animatedValue.toLocaleString()}
        </div>
        
        <div 
          className="text-gray-600 font-medium capitalize text-lg"
          style={{ fontFamily: '"Lora", serif' }}
        >
          {labelTranslated}
        </div>
      </div>
    </div>
  );
};

const Statistics = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [realStats, setRealStats] = useState(null);
  const [animatedNumbers, setAnimatedNumbers] = useState({
    lignages: 0,
    members: 0,
    bulletins: 0,
    years: 0
  });
  const { currentLanguage } = useLanguage();

  // Traductions
  const { translatedText: contextTextTranslated } = useTranslate("Depuis plus de deux siècles, les Lignages de Bruxelles préservent le riche patrimoine des familles fondatrices de la ville, rassemblant les descendants dans un esprit de tradition et de continuité historique.");

  // Charger les statistiques réelles depuis l'API
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
        const response = await fetch(`${API_BASE}/api/statistics`);
        if (response.ok) {
          const data = await response.json();
          setRealStats(data);
          console.log('✅ Statistiques réelles chargées:', data);
        } else {
          console.warn('⚠️ Impossible de charger les statistiques, utilisation des valeurs par défaut');
        }
      } catch (error) {
        console.warn('⚠️ Erreur chargement statistiques:', error);
      }
    };

    fetchStats();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('statistics-section');
    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, []);

  useEffect(() => {
    if (isVisible && realStats) {
      const targets = {
        lignages: realStats.lignages || 7,
        members: realStats.members || 37,
        bulletins: realStats.bulletins || 2,
        years: realStats.years || 28
      };

      const durations = {
        lignages: 500,
        members: 2000,
        bulletins: 1000,
        years: 2500
      };

      Object.keys(targets).forEach(key => {
        let startTime = null;
        const targetValue = targets[key];
        const duration = durations[key];

        const animate = (currentTime) => {
          if (!startTime) startTime = currentTime;
          const progress = Math.min((currentTime - startTime) / duration, 1);
          
          const easeOutCubic = 1 - Math.pow(1 - progress, 3);
          const currentValue = Math.floor(targetValue * easeOutCubic);
          
          setAnimatedNumbers(prev => ({
            ...prev,
            [key]: currentValue
          }));

          if (progress < 1) {
            requestAnimationFrame(animate);
          }
        };

        requestAnimationFrame(animate);
      });
    }
  }, [isVisible]);

  const getIcon = (iconUrl) => {
    return <img src={iconUrl} alt="Icon" className="w-8 h-8" />;
  };

  const getAnimatedValue = (index) => {
    switch (index) {
      case 0: return animatedNumbers.lignages;
      case 1: return animatedNumbers.members;
      case 2: return animatedNumbers.bulletins;
      case 3: return animatedNumbers.years;
      default: return 0;
    }
  };

  return (
    <section id="statistics-section" className="py-16 bg-gradient-to-br from-amber-50 via-white to-amber-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {mockData.statistics.map((stat, index) => {
            const animatedValue = getAnimatedValue(index);
            
            return (
              <StatCard 
                key={index} 
                stat={stat} 
                animatedValue={animatedValue} 
              />
            );
          })}
        </div>
        
        {/* Additional context */}
        <div className="text-center mt-12">
          <p 
            className="text-gray-600 max-w-3xl mx-auto leading-relaxed text-lg"
            style={{ fontFamily: '"Lora", serif' }}
          >
            {contextTextTranslated}
          </p>
        </div>
      </div>
    </section>
  );
};

export default Statistics;